import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  Button,
  Stack,
  Box,
  Typography,
  IconButton,
  Alert,
  Chip,
  Snackbar,
  CircularProgress,
  InputAdornment,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import dayjs from "dayjs";
import config from "../../config";

const BRAND_GRADIENT = "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)";
const LINE = "1px solid rgba(15,23,42,0.08)";
const PRICE_COLS = "104px repeat(3, minmax(0, 1fr))";
const BUYER_COLS = { xs: "minmax(0,1fr) 92px 34px", sm: "minmax(0,1fr) 130px 40px" };

let uidCounter = 0;
const nextUid = () => `b${++uidCounter}`;

const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const toNum = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};
const isInvalidNumber = (v) => {
  if (v === "" || v === null || v === undefined) return false; 
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isNaN(n) || n < 0;
};
const normName = (s) => String(s || "").trim().toLowerCase();
const optionKey = (o) => (o ? o._id || `name:${normName(o.buyerName)}` : "");
const varietyKey = (o) => (o ? o._id || `name:${normName(o.varietyName)}` : "");
const mandiKey = (o) => (o ? o._id || `name:${normName(o.mandiName)}` : "");

const getMandiLabel = (m) => m?.mandiLocation || "";
const getMandiSubLabel = (m) => [m?.mandiPartner, m?.state].filter(Boolean).join(", ") + (m?.mandiCode ? ` · ${m.mandiCode}` : "");

const buildInitialForm = (row) => ({
  date: row?.callDate ? dayjs(row.callDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
  mandiPartnerId: row?.mandiPartnerId || "",
  mandiPartnerName: row?.mandi && row.mandi !== "-" ? String(row.mandi) : "",
  varietyId: row?.varietyId || "",
  varietyName: row?.variety && row.variety !== "-" ? row.variety : "",
  total: toStr(row?.totalArrival ?? 0),
  handQty: toStr(row?.handQty ?? 0),
  handLo: toStr(row?.handPriceMin ?? 0),
  handHi: toStr(row?.handPriceMax ?? 0),
  combQty: toStr(row?.combineQty ?? 0),
  combLo: toStr(row?.combinePriceMin ?? 0),
  combHi: toStr(row?.combinePriceMax ?? 0),
  buyers: (row?.buyers || []).map((b) => ({
    uid: nextUid(),
    buyerId: b.buyerId || "",
    name: b.buyerName === "-" ? "" : b.buyerName || "",
    bags: toStr(b.purchaseQuantity ?? 0),
  })),
});

const isBlankBuyer = (b) => !b.name && (b.bags === "" || toNum(b.bags) === 0);

const validate = (form) => {
  const errors = {};
  if (!form.mandiPartnerName) errors.mandiPartnerName = "Select a mandi location";
  if (!form.varietyName) errors.varietyName = "Select a variety";
  ["total", "handQty", "handLo", "handHi", "combQty", "combLo", "combHi"].forEach((k) => {
    if (isInvalidNumber(form[k])) errors[k] = "Invalid";
  });
  if (!errors.handHi && !errors.handLo && toNum(form.handHi) < toNum(form.handLo)) errors.handHi = "High < low";
  if (!errors.combHi && !errors.combLo && toNum(form.combHi) < toNum(form.combLo)) errors.combHi = "High < low";

  const buyerErrors = form.buyers.map((b) => {
    if (isBlankBuyer(b)) return {};
    const e = {};
    if (!b.name) e.name = "Select a buyer";
    if (isInvalidNumber(b.bags)) e.bags = "Invalid";
    return e;
  });
  const hasBuyerErrors = buyerErrors.some((e) => Object.keys(e).length > 0);
  return { errors, buyerErrors, isValid: Object.keys(errors).length === 0 && !hasBuyerErrors };
};


const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "8px", background: "#fff", fontSize: "0.9rem" },
  "& .MuiFormHelperText-root": { mt: 0.25, mx: 0.5, fontSize: "0.68rem" },
};

const SectionTitle = ({ children, hint, right }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
    <Stack direction="row" alignItems="baseline" spacing={1}>
      <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#0F172A" }}>{children}</Typography>
      {hint && <Typography sx={{ fontSize: "0.74rem", color: "rgba(15,23,42,0.5)" }}>{hint}</Typography>}
    </Stack>
    {right}
  </Stack>
);

const Panel = ({ children }) => (
  <Box sx={{ border: LINE, borderRadius: "12px", overflow: "hidden", background: "#fff" }}>{children}</Box>
);

const HeadCell = ({ children }) => (
  <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "rgba(15,23,42,0.5)" }}>
    {children}
  </Typography>
);

const Pill = ({ color, bg, children }) => (
  <Box sx={{ px: 1.25, py: 0.4, borderRadius: "999px", fontWeight: 700, fontSize: "0.78rem", color, background: bg }}>{children}</Box>
);

const NumField = ({ label, value, onChange, error, prefix, disabled, showLabel = true }) => (
  <TextField
    fullWidth
    size="small"
    label={showLabel ? label : undefined}
    placeholder={showLabel ? undefined : "0"}
    value={value}
    onChange={onChange}
    error={Boolean(error)}
    helperText={error || undefined}
    disabled={disabled}
    type="number"
    inputProps={{ min: 0, step: "any", inputMode: "decimal", "aria-label": label }}
    onWheel={(e) => e.target.blur()}
    onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
    onFocus={(e) => e.target.select()}
    InputProps={{ startAdornment: prefix ? <InputAdornment position="start">{prefix}</InputAdornment> : undefined }}
    sx={fieldSx}
  />
);

const ReadOnlyField = ({ label, value, icon }) => (
  <TextField
    fullWidth
    size="small"
    label={label}
    value={value}
    disabled
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <Box sx={{ display: "flex", color: "rgba(15,23,42,0.4)" }}>{icon}</Box>
        </InputAdornment>
      ),
      endAdornment: (
        <InputAdornment position="end">
          <Tooltip title="Date can't be changed">
            <LockRoundedIcon sx={{ fontSize: 15, color: "rgba(15,23,42,0.3)" }} />
          </Tooltip>
        </InputAdornment>
      ),
    }}
    sx={{
      ...fieldSx,
      "& .MuiOutlinedInput-root": { ...fieldSx["& .MuiOutlinedInput-root"], background: "rgba(15,23,42,0.03)" },
      "& .Mui-disabled": { WebkitTextFillColor: "rgba(15,23,42,0.75) !important" },
    }}
  />
);


const EditManualReportDialog = ({ open, row, reportId, token, onClose, onSaved }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [form, setForm] = useState(() => buildInitialForm(row));
  const [baseline, setBaseline] = useState(() => buildInitialForm(row));
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState("");
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [toast, setToast] = useState("");
  const [focusUid, setFocusUid] = useState("");
  const [masterBuyers, setMasterBuyers] = useState([]);
  const [buyersLoading, setBuyersLoading] = useState(false);
  const [buyersError, setBuyersError] = useState("");
  const [masterVarieties, setMasterVarieties] = useState([]);
  const [varietiesLoading, setVarietiesLoading] = useState(false);
  const [varietiesError, setVarietiesError] = useState("");
  const [masterMandis, setMasterMandis] = useState([]);
  const [mandisLoading, setMandisLoading] = useState(false);
  const [mandisError, setMandisError] = useState("");

  const contentRef = useRef(null);
  const listRef = useRef(null);

  const loadBuyers = useCallback(
    async (signal) => {
      setBuyersLoading(true);
      setBuyersError("");
      try {
        const res = await fetch(`${config.apiBaseUrl}/buyers/buyer/get`, {
          method: "GET",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          signal,
        });
        if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
        const json = await res.json();
        if (json?.success === false) throw new Error(json?.message || "Failed to load buyers.");

        const list = (Array.isArray(json?.data) ? json.data : [])
          .filter((b) => b?._id && b?.buyerName)
          .map((b) => ({ _id: b._id, buyerName: String(b.buyerName).trim(), contactNumber: b.contactNumber || "" }))
          .sort((a, b) => a.buyerName.localeCompare(b.buyerName));
        setMasterBuyers(list);
      } catch (err) {
        if (err.name === "AbortError") return;
        setBuyersError(err.message || "Could not load buyers.");
      } finally {
        if (!signal?.aborted) setBuyersLoading(false);
      }
    },
    [token]
  );

  const loadVarieties = useCallback(
    async (signal) => {
      setVarietiesLoading(true);
      setVarietiesError("");
      try {
        const res = await fetch(`${config.apiBaseUrl}/variety/get`, {
          method: "GET",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          signal,
        });
        if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
        const json = await res.json();
        if (json?.success === false) throw new Error(json?.message || "Failed to load varieties.");

        const list = (Array.isArray(json?.data) ? json.data : [])
          .filter((v) => v?._id && v?.varietyName && v?.status !== false)
          .map((v) => ({ _id: v._id, varietyName: String(v.varietyName).trim(), varietyCode: v.varietyCode || "" }))
          .sort((a, b) => a.varietyName.localeCompare(b.varietyName));
        setMasterVarieties(list);
      } catch (err) {
        if (err.name === "AbortError") return;
        setVarietiesError(err.message || "Could not load varieties.");
      } finally {
        if (!signal?.aborted) setVarietiesLoading(false);
      }
    },
    [token]
  );

  const loadMandis = useCallback(
    async (signal) => {
      setMandisLoading(true);
      setMandisError("");
      try {
        const res = await fetch(`${config.apiBaseUrl}/mandi-partner/get-all`, {
          method: "GET",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          signal,
        });
        if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
        const json = await res.json();
        if (json?.success === false) throw new Error(json?.message || "Failed to load mandi partners.");

        const raw = Array.isArray(json?.data) ? json.data : [];
        const list = raw
          .filter((m) => m?._id && getMandiLabel(m) && m?.isActive !== false && m?.softDelete !== true)
          .map((m) => ({ _id: m._id, mandiName: getMandiLabel(m).trim(), sub: getMandiSubLabel(m) }))
          .sort((a, b) => a.mandiName.localeCompare(b.mandiName));
        setMasterMandis(list);
      } catch (err) {
        if (err.name === "AbortError") return;
        setMandisError(err.message || "Could not load mandi partners.");
      } finally {
        if (!signal?.aborted) setMandisLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    if (masterBuyers.length === 0) loadBuyers(controller.signal);
    if (masterVarieties.length === 0) loadVarieties(controller.signal);
    if (masterMandis.length === 0) loadMandis(controller.signal);
    return () => controller.abort();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const initial = buildInitialForm(row);
    setForm(initial);
    setBaseline(initial);
    setSubmitted(false);
    setApiError("");
    setConfirmDiscard(false);
    setFocusUid("");
  }, [open, row]);

  useEffect(() => {
    if (!masterBuyers.length) return;
    const byName = new Map(masterBuyers.map((b) => [normName(b.buyerName), b._id]));
    const hydrate = (f) => ({
      ...f,
      buyers: f.buyers.map((b) => (b.buyerId || !b.name ? b : { ...b, buyerId: byName.get(normName(b.name)) || "" })),
    });
    setForm(hydrate);
    setBaseline(hydrate);
  }, [masterBuyers, open, row]);

  useEffect(() => {
    if (!masterVarieties.length) return;
    const byName = new Map(masterVarieties.map((v) => [normName(v.varietyName), v._id]));
    const byId = new Map(masterVarieties.map((v) => [v._id, v.varietyName]));
    const hydrate = (f) => {
      if (f.varietyId && !f.varietyName) return { ...f, varietyName: byId.get(f.varietyId) || f.varietyName };
      if (!f.varietyId && f.varietyName) return { ...f, varietyId: byName.get(normName(f.varietyName)) || "" };
      return f;
    };
    setForm(hydrate);
    setBaseline(hydrate);
  }, [masterVarieties, open, row]);

  useEffect(() => {
    if (!masterMandis.length) return;
    const byName = new Map(masterMandis.map((m) => [normName(m.mandiName), m._id]));
    const byId = new Map(masterMandis.map((m) => [m._id, m.mandiName]));
    const hydrate = (f) => {
      if (f.mandiPartnerId && !f.mandiPartnerName) return { ...f, mandiPartnerName: byId.get(f.mandiPartnerId) || f.mandiPartnerName };
      if (!f.mandiPartnerId && f.mandiPartnerName) return { ...f, mandiPartnerId: byName.get(normName(f.mandiPartnerName)) || "" };
      return f;
    };
    setForm(hydrate);
    setBaseline(hydrate);
  }, [masterMandis, open, row]);

  const buyerOptions = useMemo(() => {
    const known = new Set(masterBuyers.map((b) => normName(b.buyerName)));
    const legacy = [];
    baseline.buyers.forEach((b) => {
      if (b.name && !b.buyerId && !known.has(normName(b.name)) && !legacy.some((l) => normName(l.buyerName) === normName(b.name))) {
        legacy.push({ _id: "", buyerName: b.name, contactNumber: "", legacy: true });
      }
    });
    return [...masterBuyers, ...legacy];
  }, [masterBuyers, baseline]);

  const findOption = useCallback(
    (b) => {
      if (!b.name && !b.buyerId) return null;
      return (
        buyerOptions.find((o) => (b.buyerId && o._id === b.buyerId) || (!b.buyerId && normName(o.buyerName) === normName(b.name))) || null
      );
    },
    [buyerOptions]
  );

  const takenKeys = useMemo(() => new Set(form.buyers.map((b) => optionKey(findOption(b))).filter(Boolean)), [form.buyers, findOption]);

  const varietyOptions = useMemo(() => {
    const known = new Set(masterVarieties.map((v) => normName(v.varietyName)));
    const legacy =
      baseline.varietyName && !known.has(normName(baseline.varietyName))
        ? [{ _id: "", varietyName: baseline.varietyName, varietyCode: "", legacy: true }]
        : [];
    return [...masterVarieties, ...legacy];
  }, [masterVarieties, baseline]);

  const selectedVariety = useMemo(
    () =>
      varietyOptions.find(
        (o) => (form.varietyId && o._id === form.varietyId) || (!form.varietyId && normName(o.varietyName) === normName(form.varietyName))
      ) || null,
    [varietyOptions, form.varietyId, form.varietyName]
  );

  const mandiOptions = useMemo(() => {
    const known = new Set(masterMandis.map((m) => normName(m.mandiName)));
    const legacy =
      baseline.mandiPartnerName && !known.has(normName(baseline.mandiPartnerName))
        ? [{ _id: "", mandiName: baseline.mandiPartnerName, sub: "", legacy: true }]
        : [];
    return [...masterMandis, ...legacy];
  }, [masterMandis, baseline]);

  const selectedMandi = useMemo(
    () =>
      mandiOptions.find(
        (o) =>
          (form.mandiPartnerId && o._id === form.mandiPartnerId) ||
          (!form.mandiPartnerId && normName(o.mandiName) === normName(form.mandiPartnerName))
      ) || null,
    [mandiOptions, form.mandiPartnerId, form.mandiPartnerName]
  );

  const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(baseline), [form, baseline]);
  const { errors, buyerErrors, isValid } = useMemo(() => validate(form), [form]);
  const showErr = (key) => (submitted ? errors[key] : undefined);
  const buyersTotal = useMemo(() => form.buyers.reduce((sum, b) => sum + toNum(b.bags), 0), [form.buyers]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const patchBuyer = (uid, patch) =>
    setForm((f) => ({ ...f, buyers: f.buyers.map((b) => (b.uid === uid ? { ...b, ...patch } : b)) }));
  const removeBuyer = (uid) => setForm((f) => ({ ...f, buyers: f.buyers.filter((b) => b.uid !== uid) }));
  const addBuyer = () => {
    const uid = nextUid();
    setForm((f) => ({ ...f, buyers: [...f.buyers, { uid, buyerId: "", name: "", bags: "" }] }));
    setFocusUid(uid);
    requestAnimationFrame(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }));
  };

  const requestClose = () => {
    if (saving) return;
    if (isDirty) setConfirmDiscard(true);
    else onClose();
  };

  const handleSave = async () => {
    setSubmitted(true);
    setApiError("");

    const idToUpdate = reportId || row?.id;
    if (!idToUpdate) {
      setApiError("Missing report id — this row can't be updated.");
      return;
    }
    if (!isValid) {
      contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const resolvedVarietyId =
      form.varietyId || masterVarieties.find((v) => normName(v.varietyName) === normName(form.varietyName))?._id || row.varietyId || "";
    const resolvedMandiId =
      form.mandiPartnerId ||
      masterMandis.find((m) => normName(m.mandiName) === normName(form.mandiPartnerName))?._id ||
      row.mandiPartnerId ||
      "";

    const totalArrival = toNum(form.total);

    const payload = {
      mandiPartnerName: resolvedMandiId,
      date: form.date,
      marketTotal: totalArrival, 
      varieties: [
        {
          varietyId: resolvedVarietyId,
          variety: form.varietyName,
          total: totalArrival,
          handQty: toNum(form.handQty),
          handLo: toNum(form.handLo),
          handHi: toNum(form.handHi),
          combQty: toNum(form.combQty),
          combLo: toNum(form.combLo),
          combHi: toNum(form.combHi),
          buyers: form.buyers
            .filter((b) => b.name)
            .map((b) => ({ buyerId: b.buyerId || "", name: b.name, bags: toNum(b.bags) })),
        },
      ],
    };

    setSaving(true);
    try {
      const res = await fetch(`${config.apiBaseUrl}/campaign-call/edit-manual-ivr-form/${idToUpdate}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw new Error(json?.message || `Request failed with status ${res.status}`);
      }
      setToast("Report updated successfully");
      onSaved?.();
      onClose();
    } catch (err) {
      setApiError(err.message || "Failed to update report.");
      contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSaving(false);
    }
  };

  const priceRows = [
    { key: "hand", label: "Hand", color: "#6D28D9", bg: "rgba(124,58,237,0.10)", qty: "handQty", lo: "handLo", hi: "handHi" },
    { key: "comb", label: "Combine", color: "#B45309", bg: "rgba(217,119,6,0.12)", qty: "combQty", lo: "combLo", hi: "combHi" },
  ];

  const displayDate = form.date ? dayjs(form.date).format("DD MMM YYYY") : "-";
  const headerMandiLabel = form.mandiPartnerName || row?.mandi || "-";
  const headerVarietyLabel = form.varietyName || row?.variety || "-";

  return (
    <>
      <Dialog
        open={open}
        onClose={requestClose}
        fullScreen={isMobile}
        fullWidth
        maxWidth={false}
        scroll="paper"
        aria-labelledby="edit-report-title"
        PaperProps={{ sx: { maxWidth: 680, borderRadius: { xs: 0, sm: "20px" } } }}
      >
        <DialogTitle
          id="edit-report-title"
          component="div"
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            borderBottom: LINE,
            background: "linear-gradient(135deg, rgba(37,99,235,0.07) 0%, rgba(6,182,212,0.07) 100%)",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  borderRadius: "13px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  background: BRAND_GRADIENT,
                  boxShadow: "0 6px 16px rgba(37,99,235,0.25)",
                }}
              >
                <EditRoundedIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", lineHeight: 1.2 }}>Edit report</Typography>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" rowGap={0.5} sx={{ mt: 0.6 }}>
                  <Chip
                    size="small"
                    icon={<StorefrontRoundedIcon sx={{ fontSize: 15 }} />}
                    label={headerMandiLabel}
                    sx={{ background: "rgba(22,163,74,0.12)", color: "#166534", fontWeight: 600, fontSize: "0.74rem" }}
                  />
                  <Chip
                    size="small"
                    label={`Variety ${headerVarietyLabel}`}
                    sx={{ background: "rgba(37,99,235,0.12)", color: "#1D4ED8", fontWeight: 600, fontSize: "0.74rem" }}
                  />
                  {row?.state && row.state !== "-" && (
                    <Chip size="small" label={row.state} sx={{ background: "rgba(15,23,42,0.06)", fontWeight: 600, fontSize: "0.74rem" }} />
                  )}
                </Stack>
              </Box>
            </Stack>
            <IconButton onClick={requestClose} disabled={saving} aria-label="Close" size="small" sx={{ background: "#fff", border: LINE, alignSelf: "flex-start" }}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent ref={contentRef} sx={{ px: { xs: 2, sm: 3 }, pt: "20px !important", pb: 2.5 }}>
          {apiError && (
            <Alert severity="error" onClose={() => setApiError("")} sx={{ mb: 2, borderRadius: "10px" }}>
              {apiError}
            </Alert>
          )}
          {submitted && !isValid && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: "10px" }}>
              Please fix the highlighted fields.
            </Alert>
          )}

          <SectionTitle>Report details</SectionTitle>
          <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: "repeat(2,minmax(0,1fr))" }}>
            <ReadOnlyField label="Date" value={displayDate} icon={<EventRoundedIcon sx={{ fontSize: 18 }} />} />

            <Autocomplete
              size="small"
              fullWidth
              options={mandiOptions}
              loading={mandisLoading}
              disabled={saving}
              value={selectedMandi}
              autoHighlight
              openOnFocus
              blurOnSelect
              noOptionsText="No matching mandi location"
              loadingText="Loading mandi locations…"
              getOptionLabel={(o) => o?.mandiName || ""}
              isOptionEqualToValue={(o, v) => mandiKey(o) === mandiKey(v)}
              onChange={(_, opt) =>
                setForm((f) => ({ ...f, mandiPartnerId: opt?._id || "", mandiPartnerName: opt?.mandiName || "" }))
              }
              ListboxProps={{ style: { maxHeight: 260 } }}
              renderOption={(props, o) => {
                const { key, ...rest } = props;
                const isCurrent = normName(o.mandiName) === normName(baseline.mandiPartnerName);
                return (
                  <li key={key} {...rest}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: "100%", minWidth: 0 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: "0.86rem", fontWeight: 600 }} noWrap>
                          {o.mandiName}
                        </Typography>
                        <Typography sx={{ fontSize: "0.68rem", color: "rgba(15,23,42,0.5)" }}>
                          {o.legacy ? "Not in partner list" : o.sub || " "}
                        </Typography>
                      </Box>
                      {isCurrent && (
                        <Chip label="Current" size="small" sx={{ height: 19, fontSize: "0.62rem", fontWeight: 700, color: "#1D4ED8", background: "rgba(37,99,235,0.10)", flexShrink: 0 }} />
                      )}
                    </Stack>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Mandi location"
                  placeholder="Select mandi location"
                  error={Boolean(showErr("mandiPartnerName"))}
                  helperText={
                    showErr("mandiPartnerName") ||
                    (form.mandiPartnerName && form.mandiPartnerName === baseline.mandiPartnerName ? "Current location on this report — tap to change" : undefined)
                  }
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <StorefrontRoundedIcon sx={{ fontSize: 18, color: "rgba(15,23,42,0.4)" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {mandisLoading ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={fieldSx}
                />
              )}
            />

            <Autocomplete
              size="small"
              fullWidth
              options={varietyOptions}
              loading={varietiesLoading}
              disabled={saving}
              value={selectedVariety}
              autoHighlight
              openOnFocus
              blurOnSelect
              noOptionsText="No matching variety"
              loadingText="Loading varieties…"
              getOptionLabel={(o) => o?.varietyName || ""}
              isOptionEqualToValue={(o, v) => varietyKey(o) === varietyKey(v)}
              onChange={(_, opt) =>
                setForm((f) => ({ ...f, varietyId: opt?._id || "", varietyName: opt?.varietyName || "" }))
              }
              ListboxProps={{ style: { maxHeight: 260 } }}
              renderOption={(props, o) => {
                const { key, ...rest } = props;
                const isCurrent = normName(o.varietyName) === normName(baseline.varietyName);
                return (
                  <li key={key} {...rest}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: "100%", minWidth: 0 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: "0.86rem", fontWeight: 600 }} noWrap>
                          {o.varietyName}
                        </Typography>
                        <Typography sx={{ fontSize: "0.68rem", color: "rgba(15,23,42,0.5)" }}>
                          {o.legacy ? "Not in variety list" : o.varietyCode || " "}
                        </Typography>
                      </Box>
                      {isCurrent && (
                        <Chip label="Current" size="small" sx={{ height: 19, fontSize: "0.62rem", fontWeight: 700, color: "#1D4ED8", background: "rgba(37,99,235,0.10)", flexShrink: 0 }} />
                      )}
                    </Stack>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Variety"
                  placeholder="Select variety"
                  error={Boolean(showErr("varietyName"))}
                  helperText={
                    showErr("varietyName") ||
                    (form.varietyName && form.varietyName === baseline.varietyName ? "Current variety on this report — tap to change" : undefined)
                  }
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <CategoryRoundedIcon sx={{ fontSize: 18, color: "rgba(15,23,42,0.4)" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {varietiesLoading ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={fieldSx}
                />
              )}
            />

            <NumField label="Total arrival" value={form.total} onChange={setField("total")} error={showErr("total")} disabled={saving} />
          </Box>

          {(mandisError || varietiesError) && (
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {mandisError && (
                <Alert
                  severity="error"
                  sx={{ borderRadius: "10px" }}
                  action={
                    <Button color="inherit" size="small" startIcon={<RefreshRoundedIcon />} onClick={() => loadMandis(new AbortController().signal)} sx={{ textTransform: "none" }}>
                      Retry
                    </Button>
                  }
                >
                  Couldn't load mandi partners. {mandisError}
                </Alert>
              )}
              {varietiesError && (
                <Alert
                  severity="error"
                  sx={{ borderRadius: "10px" }}
                  action={
                    <Button color="inherit" size="small" startIcon={<RefreshRoundedIcon />} onClick={() => loadVarieties(new AbortController().signal)} sx={{ textTransform: "none" }}>
                      Retry
                    </Button>
                  }
                >
                  Couldn't load varieties. {varietiesError}
                </Alert>
              )}
            </Stack>
          )}

          <Box sx={{ mt: 3 }}>
            <SectionTitle hint="Quantity in bags, price in ₹">Quantity & pricing</SectionTitle>
            <Panel>
              <Box
                sx={{
                  display: { xs: "none", sm: "grid" },
                  gridTemplateColumns: PRICE_COLS,
                  columnGap: 1.5,
                  px: 2,
                  py: 1,
                  background: "#F8FAFC",
                  borderBottom: LINE,
                }}
              >
                <HeadCell>Type</HeadCell>
                <HeadCell>Quantity</HeadCell>
                <HeadCell>Low price</HeadCell>
                <HeadCell>High price</HeadCell>
              </Box>

              {priceRows.map((r, idx) => (
                <Box
                  key={r.key}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "repeat(3,minmax(0,1fr))", sm: PRICE_COLS },
                    columnGap: 1.5,
                    rowGap: 1,
                    alignItems: "start",
                    px: 2,
                    py: 1.5,
                    borderBottom: idx < priceRows.length - 1 ? LINE : "none",
                  }}
                >
                  <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" }, display: "flex", alignItems: "center", height: { sm: 40 } }}>
                    <Pill color={r.color} bg={r.bg}>{r.label}</Pill>
                  </Box>
                  <NumField label="Quantity" showLabel={isMobile} value={form[r.qty]} onChange={setField(r.qty)} error={showErr(r.qty)} disabled={saving} />
                  <NumField label="Low price" showLabel={isMobile} prefix="₹" value={form[r.lo]} onChange={setField(r.lo)} error={showErr(r.lo)} disabled={saving} />
                  <NumField label="High price" showLabel={isMobile} prefix="₹" value={form[r.hi]} onChange={setField(r.hi)} error={showErr(r.hi)} disabled={saving} />
                </Box>
              ))}
            </Panel>
          </Box>

          <Box sx={{ mt: 3 }}>
            <SectionTitle hint={buyersLoading ? "Loading buyer list…" : "Select from your buyer list"}>Buyers</SectionTitle>

            {buyersError && (
              <Alert
                severity="error"
                sx={{ mb: 1.25, borderRadius: "10px" }}
                action={
                  <Button color="inherit" size="small" startIcon={<RefreshRoundedIcon />} onClick={() => loadBuyers(new AbortController().signal)} sx={{ textTransform: "none" }}>
                    Retry
                  </Button>
                }
              >
                Couldn't load buyers. {buyersError}
              </Alert>
            )}

            <Panel>
              <Box
                sx={{
                  display: { xs: "none", sm: "grid" },
                  gridTemplateColumns: BUYER_COLS,
                  columnGap: 1.5,
                  px: 2,
                  py: 1,
                  background: "#F8FAFC",
                  borderBottom: LINE,
                }}
              >
                <HeadCell>Buyer</HeadCell>
                <HeadCell>Bags</HeadCell>
                <span />
              </Box>

              {form.buyers.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 3.5, px: 2 }}>
                  <Typography sx={{ fontSize: "0.85rem", color: "rgba(15,23,42,0.5)" }}>No buyers added yet</Typography>
                </Box>
              ) : (
                <Box ref={listRef} sx={{ maxHeight: { xs: "32vh", sm: 236 }, overflowY: "auto" }}>
                  {form.buyers.map((b, i) => {
                    const selected = findOption(b);
                    const be = submitted ? buyerErrors[i] || {} : {};
                    return (
                      <Box
                        key={b.uid}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: BUYER_COLS,
                          columnGap: 1.5,
                          alignItems: "start",
                          px: 2,
                          py: 1.25,
                          borderBottom: i < form.buyers.length - 1 ? LINE : "none",
                        }}
                      >
                        <Autocomplete
                          size="small"
                          fullWidth
                          sx={{ minWidth: 0 }}
                          options={buyerOptions}
                          loading={buyersLoading}
                          disabled={saving}
                          value={selected}
                          autoHighlight
                          openOnFocus
                          blurOnSelect
                          noOptionsText="No matching buyer"
                          loadingText="Loading buyers…"
                          getOptionLabel={(o) => o?.buyerName || ""}
                          isOptionEqualToValue={(o, v) => optionKey(o) === optionKey(v)}
                          getOptionDisabled={(o) => takenKeys.has(optionKey(o)) && optionKey(o) !== optionKey(selected)}
                          onChange={(_, opt) =>
                            patchBuyer(b.uid, opt ? { buyerId: opt._id || "", name: opt.buyerName } : { buyerId: "", name: "" })
                          }
                          ListboxProps={{ style: { maxHeight: 240 } }}
                          renderOption={(props, o) => {
                            const { key, ...rest } = props;
                            const taken = takenKeys.has(optionKey(o));
                            return (
                              <li key={key} {...rest}>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontSize: "0.86rem", fontWeight: 600 }} noWrap>
                                    {o.buyerName}
                                  </Typography>
                                  <Typography sx={{ fontSize: "0.68rem", color: "rgba(15,23,42,0.5)" }}>
                                    {o.legacy ? "Not in buyer list" : taken ? "Already added" : o.contactNumber && o.contactNumber !== ".." ? o.contactNumber : " "}
                                  </Typography>
                                </Box>
                              </li>
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={isMobile ? `Buyer ${i + 1}` : undefined}
                              placeholder="Select buyer"
                              autoFocus={b.uid === focusUid}
                              error={Boolean(be.name)}
                              helperText={be.name || undefined}
                              inputProps={{ ...params.inputProps, "aria-label": `Buyer ${i + 1}` }}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {buyersLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                              sx={fieldSx}
                            />
                          )}
                        />
                        <NumField label="Bags" showLabel={isMobile} value={b.bags} onChange={(e) => patchBuyer(b.uid, { bags: e.target.value })} error={be.bags} disabled={saving} />
                        <Box sx={{ height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Tooltip title="Remove buyer">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => removeBuyer(b.uid)}
                                disabled={saving}
                                aria-label={`Remove buyer ${i + 1}`}
                                sx={{ color: "#B91C1C", "&:hover": { background: "rgba(220,38,38,0.08)" } }}
                              >
                                <DeleteOutlineRoundedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 1.5, py: 0.75, background: "#F8FAFC", borderTop: LINE }}
              >
                <Button
                  size="small"
                  startIcon={<AddRoundedIcon />}
                  onClick={addBuyer}
                  disabled={saving || Boolean(buyersError)}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px" }}
                >
                  Add buyer
                </Button>
                <Typography sx={{ fontSize: "0.8rem", color: "rgba(15,23,42,0.65)" }}>
                  {form.buyers.length} {form.buyers.length === 1 ? "buyer" : "buyers"} ·{" "}
                  <Box component="span" sx={{ fontWeight: 800, color: "#0F172A" }}>
                    {buyersTotal.toLocaleString("en-IN")} bags
                  </Box>
                </Typography>
              </Stack>
            </Panel>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: LINE,
            px: { xs: 2, sm: 3 },
            py: 1.5,
            justifyContent: "space-between",
            gap: 1,
            "& > :not(style) ~ :not(style)": { ml: 0 },
          }}
        >
          <Box>
            {isDirty && !saving && (
              <Chip size="small" label="Unsaved changes" sx={{ height: 24, fontWeight: 600, fontSize: "0.72rem", color: "#B45309", background: "rgba(245,158,11,0.14)" }} />
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button onClick={requestClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", px: 2 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !isDirty}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckRoundedIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                px: 3,
                boxShadow: "none",
                background: BRAND_GRADIENT,
                "&:hover": { background: "linear-gradient(135deg,#1D4ED8 0%,#0891B2 100%)", boxShadow: "none" },
                "&.Mui-disabled": { background: "rgba(15,23,42,0.08)", color: "rgba(15,23,42,0.35)" },
              }}
            >
              {saving ? "Saving..." : "Save changes"}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDiscard} onClose={() => setConfirmDiscard(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Discard changes?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "rgba(15,23,42,0.65)", fontSize: "0.9rem" }}>
            You have unsaved changes. If you close now, they will be lost.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDiscard(false)} sx={{ textTransform: "none", fontWeight: 600 }}>
            Keep editing
          </Button>
          <Button
            color="error"
            variant="contained"
            disableElevation
            onClick={() => {
              setConfirmDiscard(false);
              onClose();
            }}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={3500} onClose={() => setToast("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => setToast("")} severity="success" variant="filled" sx={{ borderRadius: "12px", width: "100%" }}>
          {toast}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditManualReportDialog;